<div class="dac-wrapper">

  <!-- §9 Controls row -->
  <drill-chart-controls
    [colorBy]="activeColorBy()"
    [timeScale]="activeTimeScale()"
    [palette]="palette()"
    (colorByChange)="onColorByChange($event)"
    (timeScaleChange)="onTimeScaleChange($event)"
    (navigate)="onNavigate($event)"
  />

  <!-- Scrollable grid area -->
  <div class="dac-scroll-wrapper">
    <table class="dac-table" role="grid" aria-label="Drill activity chart">

      <thead>
        <!-- §4 Day header row — uses native colspan for grouping -->
        <tr class="dac-day-row">
          @if (activeTimeScale() === 'month') {
            <th
              class="dac-blast-col dac-sticky-left dac-corner-th dac-month-label-th"
              scope="col"
            >
              {{ displayMonthLabel() }}
            </th>
          } @else {
            <th
              class="dac-blast-col dac-sticky-left dac-corner-th"
              rowspan="2"
              scope="col"
            >
              Blast
            </th>
          }

          @for (group of dayGroups(); track group.date) {
            <th
              class="dac-day-cell"
              [attr.colspan]="group.shiftCount"
              [class.is-weekend]="group.isWeekend"
              [class.is-today]="group.isToday"
              [style.min-width.px]="cellWidthPx() * group.shiftCount"
              scope="colgroup"
            >
              {{ group.label }}
            </th>
          }

          <th
            class="dac-totals-col dac-sticky-right dac-corner-th"
            rowspan="2"
            scope="col"
          >
            Totals
          </th>
        </tr>

        <!-- §3 Shift sub-header row -->
        <tr class="dac-shift-row">
          @if (activeTimeScale() === 'month') {
            <th
              class="dac-blast-col dac-sticky-left dac-corner-th"
              scope="col"
            >
              Blast
            </th>
          }
          @for (col of shiftColumns(); track col.id) {
            <th
              class="dac-shift-cell"
              [class.is-weekend]="col.isWeekend"
              [class.is-today]="col.isToday"
              [class.is-night]="col.isNight"
              [style.width.px]="cellWidthPx()"
              [style.min-width.px]="cellWidthPx()"
              scope="col"
            >
              {{ col.shiftLabel }}
            </th>
          }
        </tr>
      </thead>

      <tbody>
        <!-- §3 One row per blast (§12: virtual scrolling TODO for >50 rows) -->
        @for (blast of blasts(); track blast.blastId) {
          <tr class="dac-blast-row">

            <!-- Left sticky — blast info (§3 sidebar) -->
            <td
              class="dac-blast-label-cell dac-sticky-left"
              (click)="blastClick.emit(blast)"
            >
              <div class="blast-name">{{ blast.label }}</div>
              <div class="blast-zone">{{ blast.zone }}</div>
              <div class="blast-meta">{{ blast.holeCount }} holes &nbsp;·&nbsp; {{ blast.targetMeters }}m target</div>
            </td>

            <!-- §5 One data cell per shift column -->
            @for (col of shiftColumns(); track col.id) {
              <td
                class="dac-data-cell"
                [class.is-weekend]="col.isWeekend"
                [class.is-today]="col.isToday"
                [style.width.px]="cellWidthPx()"
              >
                <drill-block-cell
                  [sessions]="getSessionsForCell(blast, col)"
                  [shiftDurationHours]="shiftConfig().shiftDurationHours"
                  [colorBy]="activeColorBy()"
                  [palette]="legend()"
                  [isBlastDate]="isBlastDateCell(blast, col)"
                  (sessionClick)="sessionClick.emit($event)"
                />
              </td>
            }

            <!-- §8 Right sticky — per-blast totals -->
            <td class="dac-totals-cell dac-sticky-right">
              <div class="totals-inner">
                <div class="totals-numbers">
                  <span class="realized">{{ getBlastRealized(blast) }}m</span>
                  <span class="divider">/</span>
                  <span class="target">{{ blast.targetMeters }}m</span>
                </div>
                <div
                  class="progress-bar-track"
                  [attr.aria-label]="getBlastProgress(blast).toFixed(0) + '% drilled'"
                  role="progressbar"
                  [attr.aria-valuenow]="clamp100(getBlastProgress(blast))"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <div
                    class="progress-bar-fill"
                    [class.is-complete]="getBlastProgress(blast) >= 100"
                    [style.width.%]="clamp100(getBlastProgress(blast))"
                  ></div>
                </div>
                <div
                  class="progress-label"
                  [class.is-complete]="getBlastProgress(blast) >= 100"
                >
                  @if (getBlastProgress(blast) >= 100) {
                    ✓ {{ getBlastProgress(blast).toFixed(0) }}%
                  } @else {
                    {{ getBlastProgress(blast).toFixed(0) }}%
                  }
                </div>
              </div>
            </td>

          </tr>
        }
      </tbody>

      <!-- §7 Réalisé totals row -->
      <tfoot>
        <tr class="dac-realise-row">
          <td class="dac-blast-label-cell dac-sticky-left dac-realise-label">{{ 'DRILL_ACTIVITY_CHART.REALISE' | translate }}</td>

          @for (col of shiftColumns(); track col.id) {
            @let shiftTotal = getShiftTotal(col);
            <td
              class="dac-data-cell dac-realise-cell"
              [class.is-weekend]="col.isWeekend"
            >
              @if (shiftTotal > 0) {
                {{ shiftTotal }}m
              } @else {
                <span class="empty-cell">—</span>
              }
            </td>
          }

          <td class="dac-totals-cell dac-sticky-right dac-grand-total">
            {{ grandTotal() }}m
          </td>
        </tr>
      </tfoot>

    </table>
  </div>

  <!-- Legend bar -->
  @if (legend().length > 0) {
    <div class="dac-legend" role="list" [attr.aria-label]="(activeColorBy() === 'rig' ? 'Drill rig' : 'Operator') + ' legend'">
      @for (entry of legend(); track entry.id) {
        <span class="dac-legend-item" role="listitem">
          <span class="dac-legend-swatch" [style.background-color]="entry.color" aria-hidden="true"></span>
          <span class="dac-legend-label">{{ entry.label }}</span>
        </span>
      }
    </div>
  }

</div>
